import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock, ArrowUpDown } from 'lucide-react';
import BlogLayout from './BlogLayout';
import BlogSketch from './BlogSketch';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/blogData';
import { useBlogLang, useBlogMeta, useLocalizedPosts } from '../hooks/useBlogLang';
import { useSeo } from '../hooks/useSeo';
import { useReveal } from '../hooks/useScrollFx';

const SITE = 'https://lempole.com';

function formatDate(iso, lang) {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function BlogIndex() {
  const { lang } = useBlogLang();
  const suffix = lang === 'it' ? '' : `?lang=${lang}`;
  const m = useBlogMeta(lang);
  const posts = useLocalizedPosts(lang);
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const rootRef = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const displayedPosts = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    const sorted = [...posts].sort((a, b) => (a.date > b.date ? dir : -dir));
    return filter === 'all' ? sorted : sorted.filter((p) => p.category === filter);
  }, [filter, sortOrder, posts]);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${SITE}/diario#blog`,
    name: lang === 'en' ? 'The Lempole diary' : (lang === 'fr' ? 'Le journal de Lempole' : 'Diario di Lempole'),
    description: m.intro,
    inLanguage: lang === 'en' ? 'en' : (lang === 'fr' ? 'fr-FR' : 'it-IT'),
    url: `${SITE}/diario${suffix}`,
    publisher: { '@id': `${SITE}/#winery` },
    author: { '@id': `${SITE}/#raffaello` },
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      '@id': `${SITE}/diario/${p.slug}`,
      headline: p.title,
      description: p.excerpt,
      datePublished: p.date,
      image: p.cover,
      author: { '@id': `${SITE}/#raffaello` },
      url: `${SITE}/diario/${p.slug}${suffix}`,
    })),
  }), [m.intro, posts, lang, suffix]);

  const seoTitles = {
    it: 'Diario di Lempole — Racconti di un giovane vigneron a Lamole',
    en: 'The Lempole diary — notes from a young vigneron in Lamole',
    fr: 'Le journal de Lempole — récits d’un jeune vigneron à Lamole',
  };
  const seoKeywords = {
    it: 'diario Lempole, blog vino Chianti, viticoltura eroica Lamole, Slow Wine, Sangiovese rosato',
    en: 'Lempole diary, Chianti wine blog, heroic viticulture Lamole, Slow Wine, Sangiovese rosé',
    fr: 'journal Lempole, blog vin Chianti, viticulture héroïque Lamole, Slow Wine, Sangiovese rosé',
  };

  useSeo({
    title: seoTitles[lang] || seoTitles.it,
    description: m.intro,
    canonical: `${SITE}/diario${suffix}`,
    image: posts[0]?.cover,
    type: 'website',
    keywords: seoKeywords[lang] || seoKeywords.it,
    jsonLd,
  });

  return (
    <BlogLayout>
      <div ref={rootRef} className="blog-index">
        {/* HERO */}
        <header className="blog-hero" data-testid="blog-hero">
          <div className="blog-hero__inner reveal">
            <h1 className="blog-hero__title font-display">
              {lang === 'en' ? (
                <>The <em className="font-display-italic">Lempole</em> diary.</>
              ) : lang === 'fr' ? (
                <>Le <em className="font-display-italic">journal</em> de Lempole.</>
              ) : (
                <>Il <em className="font-display-italic">diario</em> di Lempole.</>
              )}
            </h1>
            <p className="blog-hero__lead font-body">{m.intro}</p>
          </div>
        </header>

        {/* FILTERS + SORT */}
        <div className="blog-filters reveal" role="tablist" aria-label="Filtra per categoria">
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`blog-filter ${filter === 'all' ? 'is-active' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="blog-filter-all"
          >
            Tutti
          </button>
          {Object.entries(BLOG_CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              className={`blog-filter ${filter === key ? 'is-active' : ''}`}
              onClick={() => setFilter(key)}
              data-testid={`blog-filter-${key}`}
              style={filter === key ? { color: cat.color, borderColor: cat.color } : undefined}
            >
              {cat.label}
            </button>
          ))}

          <button
            type="button"
            className="blog-sort"
            onClick={() => setSortOrder((s) => (s === 'asc' ? 'desc' : 'asc'))}
            data-testid="blog-sort-toggle"
            aria-label={`Ordina dal più ${sortOrder === 'asc' ? 'recente' : 'vecchio'}`}
          >
            <ArrowUpDown size={12} />
            <span>{sortOrder === 'asc' ? 'più vecchi' : 'più recenti'}</span>
          </button>
        </div>

        {/* GRID */}
        <ul className="blog-grid" data-testid="blog-grid">
          {displayedPosts.map((p, i) => {
            const cat = BLOG_CATEGORIES[p.category];
            return (
              <li key={p.slug} className="reveal" style={{ transitionDelay: `${(i % 3) * 90}ms` }}>
                <Link
                  to={`/diario/${p.slug}${suffix}`}
                  className="blog-card"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="blog-card__cover blog-card__cover--sketch">
                    <BlogSketch slug={p.slug} ariaLabel={p.title} />
                  </div>
                  <div className="blog-card__body">
                    <span className="blog-card__cat font-kicker" style={{ color: cat.color }}>
                      {cat.label}
                    </span>
                    <h3 className="blog-card__title font-display">{p.title}</h3>
                    <p className="blog-card__excerpt">{p.excerpt}</p>
                    <div className="blog-card__foot">
                      <time dateTime={p.date}>{formatDate(p.date, lang)}</time>
                      <span className="blog-card__dot" aria-hidden="true">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {p.readMinutes} {m.minRead}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="blog-back reveal">
          <Link to={`/${suffix}`} className="blog-back__link font-display-italic" data-testid="blog-index-back-site">
            {m.backToSite}
          </Link>
        </div>
      </div>
    </BlogLayout>
  );
}
